'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { htmlFormatterContent } from '../../data/html-formatter-content';
import Link from 'next/link';
import beautify from 'js-beautify';

export default function HtmlFormatter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [indent, setIndent] = useState(2);

  useEffect(() => {
    const savedInput = localStorage.getItem('htmlFormatterInput');
    if (savedInput) {
      setInput(savedInput);
    }
  }, []);

  const formatHtml = () => {
    try {
      if (!input.trim()) {
        setError('Please enter some HTML to format');
        return;
      }

      // Use js-beautify for proper HTML formatting
      const formatted = beautify.html(input, {
        indent_size: indent === 0 ? 0 : indent,
        indent_char: ' ',
        max_preserve_newlines: 2,
        preserve_newlines: true,
        wrap_line_length: 0,
        end_with_newline: true,
        unformatted: ['pre', 'code', 'textarea'],
        content_unformatted: ['script', 'style'],
      });

      setOutput(formatted);
      setError('');

      localStorage.setItem('htmlFormatterInput', input);
      toast.success('HTML formatted successfully!');
    } catch (err) {
      setError('Error formatting HTML: ' + (err as Error).message);
      setOutput('');
    }
  };

  const copyToClipboard = () => {
    if (!output) return;

    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    localStorage.removeItem('htmlFormatterInput');
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8 text-center">
          <ToolPageIntro title="HTML Formatter & Beautifier">
                Format, beautify, and validate HTML code with our free online HTML formatter. Make your HTML readable and well-structured with proper indentation.
            </ToolPageIntro>
        </div>

        {/* Main tool card – full width */}
        <div className="space-y-6">
          {/* Tool Interface */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-left">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                    Format your HTML
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Paste HTML on the left, get clean, formatted output on the right.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearAll}
                    className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={formatHtml}
                    className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-indigo-500/30 transition hover:bg-[#4338CA] hover:shadow-md"
                  >
                    Format HTML
                  </button>
                </div>
              </div>
            </div>

            <div className="grid gap-0 border-t border-slate-100 lg:grid-cols-2 dark:border-slate-800">
              {/* Input */}
              <div className="flex min-h-[340px] flex-col border-b border-slate-100 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-950/60 lg:border-b-0 lg:border-r">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label
                    htmlFor="input"
                    className="text-xs font-medium text-slate-700 dark:text-slate-200"
                  >
                    Input HTML
                  </label>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Client-side · Private
                  </span>
                </div>
                <div className="relative flex-1">
                  <textarea
                    id="input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="h-full w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-900 shadow-inner focus:border-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-50 dark:shadow-none"
                    placeholder='Paste your HTML here...'
                    spellCheck="false"
                  />
                </div>
              </div>

              {/* Output */}
              <div className="flex min-h-[340px] flex-col bg-slate-50/80 p-4 dark:bg-slate-950/60">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label
                    htmlFor="output"
                    className="text-xs font-medium text-slate-700 dark:text-slate-200"
                  >
                    Formatted HTML
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <label
                        htmlFor="indent"
                        className="text-[11px] text-slate-500 dark:text-slate-400"
                      >
                        Indent
                      </label>
                      <select
                        id="indent"
                        value={indent}
                        onChange={(e) => setIndent(Number(e.target.value))}
                        className="rounded-full border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 focus:border-[#4F46E5] focus:outline-none focus:ring-1 focus:ring-[#4F46E5]/40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      >
                        <option value={2}>2 spaces</option>
                        <option value={4}>4 spaces</option>
                        <option value={0}>Minify</option>
                      </select>
                    </div>
                    <button
                      onClick={copyToClipboard}
                      disabled={!output}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs transition ${output
                        ? 'border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-900 dark:border-slate-600 dark:text-slate-200 dark:hover:border-slate-400'
                        : 'cursor-not-allowed border-slate-200 text-slate-300 dark:border-slate-700 dark:text-slate-600'
                        }`}
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="relative flex-1">
                  <pre className="h-full w-full overflow-auto rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-50">
                    <code>
                      {output || (error ? '' : 'Formatted HTML will appear here...')}
                    </code>
                  </pre>
                  {error && (
                    <div className="pointer-events-auto absolute inset-x-2 bottom-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-400">
              <h3 className="mb-1 font-medium text-slate-800 dark:text-slate-100">
                How to use the HTML Formatter
              </h3>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Paste your HTML in the left panel</li>
                <li>Click the &quot;Format HTML&quot; button to format the HTML</li>
                <li>Use the indentation selector to adjust the output format</li>
                <li>Click the copy icon to copy the formatted HTML to clipboard</li>
              </ol>
            </div>
          </div>

          {/* Secondary cards row: Quick Tips + Related tools */}
          <div className="grid gap-5 md:grid-cols-2">
            {/* Quick Tips Card */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/80 p-5 text-slate-900 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-50">
              <h3 className="mb-3 text-sm font-semibold">Quick Tips</h3>
              <ul className="space-y-2 text-xs">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Use semantic HTML5 elements for better structure</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Maintain consistent indentation throughout your code</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Validate HTML with W3C validator after formatting</span>
                </li>
              </ul>
            </div>

            {/* Related Tools Card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-50">
              <h3 className="mb-3 text-sm font-semibold">Related Tools</h3>
              <ul className="space-y-3 text-xs">
                {htmlFormatterContent.relatedTools.map((tool, index) => (
                  <li key={index}>
                    <Link
                      href={tool.url}
                      className="flex items-center text-[#4F46E5] transition hover:text-[#4338CA] dark:text-indigo-300 dark:hover:text-indigo-200"
                    >
                      <svg
                        className="mr-2 h-4 w-4 text-[#4F46E5] dark:text-indigo-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      {tool.name}
                    </Link>
                    <p className="ml-6 mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {tool.description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Informational / SEO Content */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <InformationalContent
              title={htmlFormatterContent.title}
              description={htmlFormatterContent.description}
              content={htmlFormatterContent.content}
              faqs={htmlFormatterContent.faqs}
              relatedTools={htmlFormatterContent.relatedTools}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
